import React, { useRef, useState } from 'react';

import CommunityGuidelinesModal from './CommunityGuidelinesModal';
import {
  BlockingCategory,
  WritingAssistContext,
} from './WritingAssistMoments.types';
import usePrefersReducedMotion from './usePrefersReducedMotion';
import {
  ActionButton,
  ActionRow,
  LinkButton,
  MomentPanel,
} from './WritingAssistMoments.styles';

export interface HardStopProps {
  context: WritingAssistContext;
  category: BlockingCategory;
  excerpt: string;
  onEdit: () => void;
}

const SURFACE: Record<WritingAssistContext, string> = {
  aboutMe: 'bio',
  baseCoverLetter: 'cover letter',
  applicationCoverLetter: 'cover letter',
  jobDescription: 'job posting',
};

const HardStop: React.FC<HardStopProps> = ({ context, category, excerpt, onEdit }) => {
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const guidelineButton = useRef<HTMLButtonElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const openGuidelines = () => setGuidelinesOpen(true);
  const closeGuidelines = () => {
    setGuidelinesOpen(false);
    window.setTimeout(() => guidelineButton.current?.focus(), 0);
  };
  const link = (
    <LinkButton ref={guidelineButton} type="button" onClick={openGuidelines}>
      community guidelines
    </LinkButton>
  );

  let body: React.ReactNode;
  if (category === 'offsite_steering') {
    body = (
      <p>
        This part reads as arranging work or payment outside TempStars: &quot;{excerpt}&quot;.
        That&apos;s against our {link} — they keep TempStars fair and protected for everyone.
        Rework that part and you&apos;re good to go.
      </p>
    );
  } else if (category === 'competitor') {
    body = (
      <p>
        This part promotes another staffing or hiring service: &quot;{excerpt}&quot;.
        Profiles and postings are for your work — not for other platforms. See our {link},
        rework that part, and you&apos;re good to go.
      </p>
    );
  } else if (category === 'disparagement') {
    body = (
      <p>
        This part runs down TempStars rather than describing {context === 'jobDescription' ? 'the role' : 'you'}:
        &quot;{excerpt}&quot;. A public {SURFACE[context]} isn&apos;t the place for it — see our {link}.
        Rework that part and you&apos;re good to go.
      </p>
    );
  } else {
    body = (
      <p>
        This part contains language that doesn&apos;t belong on a professional {SURFACE[context]}:
        &quot;{excerpt}&quot;. See our {link}, rework that part, and you&apos;re good to go.
      </p>
    );
  }

  return (
    <>
      <MomentPanel
        $warning
        role="alert"
        style={{ transition: reducedMotion ? 'none' : 'opacity 160ms ease' }}
      >
        <h2>We can&apos;t save this as written</h2>
        {body}
        <ActionRow>
          <ActionButton type="button" onClick={onEdit}>Edit my text</ActionButton>
        </ActionRow>
      </MomentPanel>
      <CommunityGuidelinesModal
        open={guidelinesOpen}
        category={category}
        onClose={closeGuidelines}
      />
    </>
  );
};

export default HardStop;
